import { useMemo } from 'react';
import { registerMutation, registerQuery, useQuery, useMutation } from 'academy-common-lib/utils/DataFetchingClient';
import { useGetEventByIdQuery } from '../../../__generated__/chirp/com/hubspot/academy/learning/events/rpc/LearningEventUserRpc.hooks';
import { useRegisterUserToEventSessionMutation } from '../../../__generated__/chirp/com/hubspot/academy/learning/events/rpc/EventRegistrationRpc.hooks';
import { useGetProfessorsQuery } from '../../../__generated__/chirp/com/hubspot/academy/learning/events/rpc/ProfessorRpc.hooks';
import { getMockFullLiveEvent } from './liveEventMockData';
const MOCK_DELAY = 300;
const delay = data => new Promise(resolve => setTimeout(() => resolve(data), MOCK_DELAY));
const GET_MOCK_ASSIGNMENTS = registerQuery({
  fieldName: 'getMockAssignments',
  args: ['liveEventId'],
  fetcher({
    liveEventId
  }) {
    return delay(getMockFullLiveEvent(liveEventId).assignments);
  }
});
const REGISTER_FOR_SESSION = registerMutation({
  fieldName: 'registerForSession',
  args: ['sessionId'],
  fetcher({
    sessionId
  }) {
    return delay({
      id: Date.now(),
      userId: 1,
      sessionId,
      status: 'registered',
      attended: false,
      joinLink: 'https://zoom.us/j/mock-join-link',
      zoomRegistrantId: `mock-registrant-${Date.now()}`
    });
  }
});
const CANCEL_REGISTRATION = registerMutation({
  fieldName: 'cancelRegistration',
  args: ['sessionId'],
  fetcher({
    sessionId
  }) {
    return delay({
      id: Date.now(),
      userId: 1,
      sessionId,
      status: 'cancelled',
      attended: false,
      joinLink: '',
      zoomRegistrantId: ''
    });
  }
});
export const useLearningEvent = eventId => {
  var _data$sessions;
  const {
    data,
    loading,
    error
  } = useGetEventByIdQuery({
    variables: {
      eventId
    },
    skip: !eventId
  });
  const sessions = ((_data$sessions = data === null || data === void 0 ? void 0 : data.sessions) !== null && _data$sessions !== void 0 ? _data$sessions : []).filter(s => !s.deletedAt && s.startTimeMs !== undefined);
  return {
    learningEvent: data,
    sessions,
    loading,
    error
  };
};
export const useEventInstructors = professorIds => {
  const {
    data,
    loading,
    error
  } = useGetProfessorsQuery({
    variables: {
      professorIds
    },
    skip: professorIds.length === 0
  });
  const idsKey = professorIds.join(',');
  const professors = useMemo(() => {
    var _data$professors;
    const professorsById = (_data$professors = data === null || data === void 0 ? void 0 : data.professors) !== null && _data$professors !== void 0 ? _data$professors : {};
    return professorIds.map(id => professorsById[String(id)]).filter(p => p !== undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, idsKey]);
  return {
    professors,
    loading,
    error
  };
};
export const useRegisterUserToEventSession = ({
  onCompleted,
  onError
} = {}) => {
  const [mutate, {
    data,
    loading,
    error
  }] = useRegisterUserToEventSessionMutation({
    onCompleted: onCompleted ? wrapped => onCompleted(wrapped.result) : undefined,
    onError
  });
  return {
    registerUserToEventSession: eventSessionId => mutate({
      variables: {
        eventSessionExternalId: eventSessionId
      }
    }),
    eventSessionRegistrationData: data,
    eventSessionRegistrationLoading: loading,
    eventSessionRegistrationError: error
  };
};
export const useFullLiveEvent = liveEventId => {
  const {
    learningEvent,
    sessions,
    loading: eventLoading,
    error: eventError
  } = useLearningEvent(liveEventId);
  const {
    data: assignmentsData,
    loading: assignmentsLoading,
    error: assignmentsError
  } = useQuery(GET_MOCK_ASSIGNMENTS, {
    variables: {
      liveEventId
    }
  });
  return {
    assignments: assignmentsData !== null && assignmentsData !== void 0 ? assignmentsData : [],
    error: eventError !== null && eventError !== void 0 ? eventError : assignmentsError,
    liveEvent: learningEvent,
    loading: eventLoading || assignmentsLoading,
    sessions
  };
};
export const useRegisterForSession = ({
  onCompleted,
  onError
} = {}) => {
  const [mutate, {
    data,
    loading,
    error
  }] = useMutation(REGISTER_FOR_SESSION, {
    onCompleted,
    onError
  });
  return {
    registerForSession: sessionId => mutate({
      variables: {
        sessionId
      }
    }),
    registrationData: data,
    registrationLoading: loading,
    registrationError: error
  };
};
export const useCancelRegistration = ({
  onError
} = {}) => {
  const [mutate, {
    data,
    loading,
    error
  }] = useMutation(CANCEL_REGISTRATION, {
    onError
  });
  return {
    cancelRegistration: sessionId => mutate({
      variables: {
        sessionId
      }
    }),
    cancellationData: data,
    cancellationLoading: loading,
    cancellationError: error
  };
};